import { createContext, useContext, useState, useCallback, useMemo } from "react";

/**
 * TutorialContext — drives the live in-app spotlight tour.
 *
 * Mounted ONCE at app root (App.js). The overlay component
 * (<TutorialOverlay />) consumes this context and renders the actual
 * dark layer + spotlight + tooltip on top of the live app. Pages can
 * trigger the tour by calling `startTour()` from anywhere.
 *
 * Why a dedicated context (not just URL state):
 *   • The tour persists across `useNavigate()` page changes — the
 *     overlay stays mounted at root and re-positions its spotlight
 *     onto the new page's target after every navigation. URL state
 *     would force a remount cycle.
 *   • Skip / Finish actions need to dispatch a backend call
 *     (`completeOnboarding`) and that's cleaner from a single context
 *     than from a route handler.
 */
const TutorialContext = createContext(null);

export function TutorialProvider({ children }) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);

  const start = useCallback(() => {
    setStep(0);
    setActive(true);
  }, []);

  const next = useCallback(() => {
    setStep((s) => s + 1);
  }, []);

  const goTo = useCallback((idx) => {
    setStep(idx);
  }, []);

  const stop = useCallback(() => {
    setActive(false);
    setStep(0);
  }, []);

  const value = useMemo(
    () => ({ active, step, start, next, goTo, stop }),
    [active, step, start, next, goTo, stop]
  );

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    // Safe fallback so components that import this don't crash if the
    // provider isn't mounted yet (e.g. during SSR or in isolated tests).
    return { active: false, step: 0, start: () => {}, next: () => {}, goTo: () => {}, stop: () => {} };
  }
  return ctx;
}
